import { ExamState, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Exam State`;

type SharedKeys = Pick<
	LocDef,
	'type' | 'portal' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: false,
	feature: HubFeature.Exam,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type CPExamStateLocs =
	| 'CPExamStateEnumCorrupted'
	| 'CPExamStateEnumHidden'
	| 'CPExamStateEnumInvalid'
	| 'CPExamStateEnumOk'
	| 'CPExamStateEnumUnderReview';

export type SPExamStateLocs =
	| 'SPExamStateEnumCorrupted'
	| 'SPExamStateEnumHidden'
	| 'SPExamStateEnumInvalid'
	| 'SPExamStateEnumOk'
	| 'SPExamStateEnumUnderReview';

export const cpExamStateEnumLocs: LocDefs<CPExamStateLocs> = {
	CPExamStateEnumCorrupted: {
		defaultValue: 'Corrupted',
		displayPath: `${dpRoot}|Exam Grade Label Corrupted`,
		description: `System value for '${ExamState.Corrupted}' exam grade label`,
		legacyName: 'Exam/System.Enum.ExamState.Corrupted',
		...sharedCpLocData,
	},
	CPExamStateEnumHidden: {
		defaultValue: 'Hidden',
		displayPath: `${dpRoot}|${ExamState.Hidden}`,
		description: `System value for '${ExamState.Hidden}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.Hidden',
		...sharedCpLocData,
	},
	CPExamStateEnumInvalid: {
		defaultValue: 'Invalid',
		displayPath: `${dpRoot}|${ExamState.Invalid}`,
		description: `System value for '${ExamState.Invalid}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.Invalid',
		...sharedCpLocData,
	},
	CPExamStateEnumOk: {
		defaultValue: 'Ok',
		displayPath: `${dpRoot}|${ExamState.Ok}`,
		description: `System value for '${ExamState.Ok}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.Ok',
		...sharedCpLocData,
	},
	CPExamStateEnumUnderReview: {
		defaultValue: 'Under Review',
		displayPath: `${dpRoot}|${ExamState.UnderReview}`,
		description: `System value for '${ExamState.UnderReview}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.UnderReview',
		...sharedCpLocData,
	},
};

export const spExamStateEnumLocs: LocDefs<SPExamStateLocs> = {
	SPExamStateEnumCorrupted: {
		defaultValue: 'Corrupted',
		displayPath: `${dpRoot}|Exam Grade Label Corrupted`,
		description: `System value for '${ExamState.Corrupted}' exam grade label`,
		legacyName: 'Exam/System.Enum.ExamState.Corrupted',
		...sharedSpLocData,
	},
	SPExamStateEnumHidden: {
		defaultValue: 'Hidden',
		displayPath: `${dpRoot}|${ExamState.Hidden}`,
		description: `System value for '${ExamState.Hidden}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.Hidden',
		...sharedSpLocData,
	},
	SPExamStateEnumInvalid: {
		defaultValue: 'Invalid',
		displayPath: `${dpRoot}|${ExamState.Invalid}`,
		description: `System value for '${ExamState.Invalid}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.Invalid',
		...sharedSpLocData,
	},
	SPExamStateEnumOk: {
		defaultValue: 'Ok',
		displayPath: `${dpRoot}|${ExamState.Ok}`,
		description: `System value for '${ExamState.Ok}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.Ok',
		...sharedSpLocData,
	},
	SPExamStateEnumUnderReview: {
		defaultValue: 'Under Review',
		displayPath: `${dpRoot}|${ExamState.UnderReview}`,
		description: `System value for '${ExamState.UnderReview}' exam state`,
		legacyName: 'Exam/System.Enum.ExamState.UnderReview',
		...sharedSpLocData,
	},
};

export const EXAM_STATE_ENUM_LOCS = MapLocKeys<CPExamStateLocs | SPExamStateLocs>({
	...cpExamStateEnumLocs,
	...spExamStateEnumLocs,
});

export type CPExamStateEnumLocType = LocMap<CPExamStateLocs>;
export type SPExamStateEnumLocType = LocMap<SPExamStateLocs>;

export const cpExamStateKeys: { [key in ExamState]: keyof CPExamStateEnumLocType } = {
	Hidden: EXAM_STATE_ENUM_LOCS.CPExamStateEnumHidden,
	Corrupted: EXAM_STATE_ENUM_LOCS.CPExamStateEnumCorrupted,
	Invalid: EXAM_STATE_ENUM_LOCS.CPExamStateEnumInvalid,
	OK: EXAM_STATE_ENUM_LOCS.CPExamStateEnumOk,
	UnderReview: EXAM_STATE_ENUM_LOCS.CPExamStateEnumUnderReview,
};

export const spExamStateKeys: { [key in ExamState]: keyof SPExamStateEnumLocType } = {
	Hidden: EXAM_STATE_ENUM_LOCS.SPExamStateEnumHidden,
	Corrupted: EXAM_STATE_ENUM_LOCS.SPExamStateEnumCorrupted,
	Invalid: EXAM_STATE_ENUM_LOCS.SPExamStateEnumInvalid,
	OK: EXAM_STATE_ENUM_LOCS.SPExamStateEnumOk,
	UnderReview: EXAM_STATE_ENUM_LOCS.SPExamStateEnumUnderReview,
};
